// plugins/ar-media-query.ts
import { debounce } from 'debounce'
import { reactive, computed } from 'vue'
import { defineNuxtPlugin } from 'nuxt/app'

enum ScreensizeMapKeyEnum {
  Xs = 'xs',
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
  Xl = 'xl',
}

export type ArMediaQuerySize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

type ScreensizeMap = {
  [key in ScreensizeMapKeyEnum]: { min: number, max: number };
}

const screensizeMap: ScreensizeMap = {
  xs: { min: 0, max: 575 },
  sm: { min: 576, max: 767 },
  md: { min: 768, max: 991 },
  lg: { min: 992, max: 1199 },
  xl: { min: 1200, max: 9999 },
}

type WindowPayload = {
  windowWidth: number
  pageContentWidth: number
}

function checkIsTouchDevice() {
  if (import.meta.client) {
    const prefixes = ' -webkit- -moz- -o- -ms- '.split(' ')
    const mq = (query: string) => window.matchMedia(query).matches
    if (('ontouchstart' in window) || (window as any).DocumentTouch && document instanceof (window as any).DocumentTouch) {
      return true
    }
    // cspell:ignore heartz
    const query = ['(', prefixes.join('touch-enabled),('), 'heartz', ')'].join('')
    return mq(query)
  }
  return false
}

const isTouchDevice = checkIsTouchDevice()

const getSize = (width: number): ArMediaQuerySize => {
  const sizeKeys: ArMediaQuerySize[] = ['xs', 'sm', 'md', 'lg', 'xl']
  if (width === 0) return sizeKeys[0]
  for (const sizeKey of sizeKeys) {
    if (screensizeMap[sizeKey].min < width && screensizeMap[sizeKey].max >= width) {
      return sizeKey
    }
  }
  return 'xs'
}

const minWidth = (width: number, size: ArMediaQuerySize) => width >= screensizeMap[size].min
const maxWidth = (width: number, size: ArMediaQuerySize) => width <= screensizeMap[size].max
const only = (width: number, size: ArMediaQuerySize) => minWidth(width, size) && maxWidth(width, size)
const between = (width: number, sizeA: ArMediaQuerySize, sizeB: ArMediaQuerySize) =>
  minWidth(width, sizeA) && maxWidth(width, sizeB)

function createArMediaQuery() {
  const state = reactive({
    windowWidth: import.meta.client ? window.innerWidth : 0,
    pageContentWidth: import.meta.client ? window.innerWidth : 0,
  })

  const arMediaQuery = {
    isTouchDevice,
    sizeMap: screensizeMap,
    pageContent: {
      minWidth: (size: ArMediaQuerySize) => minWidth(state.pageContentWidth, size),
      maxWidth: (size: ArMediaQuerySize) => maxWidth(state.pageContentWidth, size),
      only: (size: ArMediaQuerySize) => only(state.pageContentWidth, size),
      between: (sizeA: ArMediaQuerySize, sizeB: ArMediaQuerySize) => between(state.pageContentWidth, sizeA, sizeB),
    },
    window: {
      minWidth: (size: ArMediaQuerySize) => minWidth(state.windowWidth, size),
      maxWidth: (size: ArMediaQuerySize) => maxWidth(state.windowWidth, size),
      only: (size: ArMediaQuerySize) => only(state.windowWidth, size),
      between: (sizeA: ArMediaQuerySize, sizeB: ArMediaQuerySize) => between(state.windowWidth, sizeA, sizeB),
    },
  }

  const updateArMediaQuery = debounce((payload: WindowPayload): void => {
    state.windowWidth = payload.windowWidth
    state.pageContentWidth = payload.pageContentWidth
  }, 50)

  return {
    arMediaQuery,
    updateArMediaQuery,
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { arMediaQuery, updateArMediaQuery } = createArMediaQuery()

  nuxtApp.provide('arMediaQuery', arMediaQuery)
  nuxtApp.provide('updateArMediaQuery', updateArMediaQuery)
})
